import { BannerService, ImageConversionService } from '@core/services';
import { Pagination } from '@core/entities';
import { map } from 'rxjs/operators';
import { BannerEntity, BannersListEntity } from '@core/entities/banners.entity';
import { plainToClass } from 'class-transformer';
import { inject } from '@angular/core';
import { BannerCategory, SectionType } from '@core/enums';
import { GenerateImageUrlWithToken } from '@core/utils';

export class UseBanner {
  private readonly _bannerService = inject(BannerService);
  private readonly _imageConversionService = inject(ImageConversionService);

  getAllBanners(pagination: Pagination) {
    return this._bannerService.find(pagination).pipe(
      map((res) => {
        if (res && res.banner && res.banner.length > 0) {
          return {
            pagination: res.pagination,
            banners: res.banner.map((item: BannerEntity) => {
              const bannerEntity = plainToClass(BannerEntity, item);
              bannerEntity.category = this._mapCategoryNumbersToNames(bannerEntity.category);
              if (bannerEntity.id) {
                bannerEntity.imageUrl = GenerateImageUrlWithToken(bannerEntity.id, SectionType.BANNER);
              }
              return bannerEntity;
            }),
          } as BannersListEntity;
        }
        return null;
      }),
    );
  }

  getBannerById(id: number) {
    return this._bannerService.get(id).pipe(
      map((res: BannerEntity) => {
        if (res) {
          const bannerEntity = plainToClass(BannerEntity, res);
          if (bannerEntity.id) {
            bannerEntity.imageUrl = GenerateImageUrlWithToken(bannerEntity.id, SectionType.BANNER);
          }
          return bannerEntity;
        }
        return null;
      }),
    );
  }

  addBanner(formData: FormData) {
    return this._bannerService.create(formData);
  }

  updateBanner(id: number, formData: FormData) {
    return this._bannerService.update(id, formData);
  }

  deleteBanner(id: number) {
    return this._bannerService.delete(id);
  }

  getDesktopBannerStatistics() {
    return this._bannerService.bannerWebStatistics().pipe(
      map((res) => {
        const banners = res.banner;

        if (banners && banners.length > 0) {
          return banners.map((item: BannerEntity) => {
            const bannerEntity = plainToClass(BannerEntity, item);
            bannerEntity.category = this._mapCategoryNumbersToNames(bannerEntity.category);
            return bannerEntity;
          });
        }

        return null;
      }),
    );
  }

  getMobileBannerStatistics() {
    return this._bannerService.bannerMobileStatistics().pipe(
      map((res) => {
        const banners = res.banner;

        if (banners && banners.length > 0) {
          return banners.map((item: BannerEntity) => {
            const bannerEntity = plainToClass(BannerEntity, item);
            bannerEntity.category = this._mapCategoryNumbersToNames(bannerEntity.category);
            return bannerEntity;
          });
        }

        return null;
      }),
    );
  }

  private _mapCategoryNumbersToNames(numbers: number[]): string[] {
    return numbers.map((number) => {
      switch (number) {
        case BannerCategory.AppSponsor:
          return 'AppSponsor';
        case BannerCategory.Sponsor:
          return 'Sponsor';
        case BannerCategory.FreeSpace:
          return 'FreeSpace';
        default:
          return 'Unknown';
      }
    });
  }
}
